import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { addMonths, format } from "date-fns";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import SectionCard from "@/components/common/SectionCard";
import EmptyState from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetProjectedCompensation } from "@/hooks/useCompensation";
import { formatUtcDate } from "@/utils/common.utils";
import { formatMoney } from "@/utils/deals.utils";

// The backend serves this month and up to 6 ahead.
const MONTHS_AHEAD = 6;
const ALL_AGENTS = "all";

/** Month grid with year arrows; months outside `first`..`last` (YYYY-MM) are disabled. */
function MonthPicker({ value, onChange, first, last }: {
  value: string; onChange: (month: string) => void; first: string; last: string;
}) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(Number(value.slice(0, 4)));
  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (next) setYear(Number(value.slice(0, 4))); }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="dashboardOutline" className="h-10 !rounded-xl" aria-label="Month" data-testid="projected-comp-month">
          <CalendarDays data-icon="inline-start" /> {format(new Date(`${value}-01T12:00:00`), "MMMM yyyy")} <ChevronDown data-icon="inline-end" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-xl border-dash-border p-3">
        <div className="mb-2 flex items-center justify-between">
          <Button type="button" variant="ghost" size="icon" aria-label="Previous year" disabled={year <= Number(first.slice(0, 4))} onClick={() => setYear(year - 1)}><ChevronLeft /></Button>
          <span className="text-sm font-semibold text-dash-ink">{year}</span>
          <Button type="button" variant="ghost" size="icon" aria-label="Next year" disabled={year >= Number(last.slice(0, 4))} onClick={() => setYear(year + 1)}><ChevronRight /></Button>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, i) => {
            const month = `${year}-${String(i + 1).padStart(2, "0")}`;
            return (
              <Button
                key={month}
                type="button"
                size="sm"
                variant={month === value ? "default" : "ghost"}
                disabled={month < first || month > last}
                onClick={() => { onChange(month); setOpen(false); }}
                data-testid={`projected-comp-month-${month}`}
              >
                {format(new Date(year, i, 1), "MMM")}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Reports → Projected compensation (client feedback, item 10): what closes in a
 * month and what the agent earns on it, by scheduled closing date. Canceled
 * deals are left out on the server. Agents see their own deals; owners,
 * brokerage admins and team leads see everyone's and can filter by agent.
 * Coordinators get nothing drawn at all.
 */
export default function ProjectedCompensation() {
  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: MONTHS_AHEAD + 1 }, (_, i) => {
      const d = addMonths(new Date(now.getFullYear(), now.getMonth(), 1), i);
      return { value: format(d, "yyyy-MM"), label: i === 0 ? `This month (${format(d, "MMMM")})` : format(d, "MMMM yyyy") };
    });
  }, []);
  const [month, setMonth] = useState(months[0].value);
  const [agent, setAgent] = useState(ALL_AGENTS);
  const { data } = useGetProjectedCompensation({ month, agent_id: agent === ALL_AGENTS ? undefined : agent });

  if (!data?.visible) return null;
  const monthLabel = months.find((m) => m.value === month)?.label ?? month;

  return (
    <SectionCard
      title="Projected compensation"
      description="What you're set to earn, by each deal's scheduled closing date. Canceled deals aren't counted."
      data-testid="reports-projected-compensation"
      action={
        <div className="flex flex-wrap items-center gap-2">
          {data.can_filter_agents && (
            <Select value={agent} onValueChange={setAgent}>
              <SelectTrigger className="h-10 w-44 rounded-xl" aria-label="Agent" data-testid="projected-comp-agent"><SelectValue /></SelectTrigger>
              <SelectContent align="end">
                <SelectItem value={ALL_AGENTS}>All agents</SelectItem>
                {data.agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || "Unnamed agent"}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <MonthPicker value={month} onChange={setMonth} first={months[0].value} last={months[months.length - 1].value} />
        </div>
      }
    >
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-dash-border pb-4">
        <div>
          <p className="text-xs text-dash-muted">Total for {monthLabel.replace(/^This month \((.*)\)$/, "$1")}</p>
          <p className="mt-1 text-3xl font-semibold tabular-nums text-dash-ink" data-testid="projected-comp-total">{formatMoney(data.total) || "$0"}</p>
        </div>
        <span className="inline-flex items-center gap-1 text-xs text-dash-muted"><Lock className="size-3.5" aria-hidden /> Private · never shown to clients</span>
      </div>

      {data.deals.length === 0 ? (
        <EmptyState compact title="Nothing closing this month" description="Deals under contract appear here once they have a closing date in this month." className="border-0" />
      ) : (
        <>
          <ul className="divide-y divide-dash-border" data-testid="projected-comp-deals">
            {data.deals.map((row) => (
              <li key={row.deal_id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-3 text-sm">
                <div className="min-w-0">
                  <Link to={`/deals/${row.deal_id}`} className="break-words font-medium text-dash-ink hover:text-dash-greenDeep">{row.address || "Untitled deal"}</Link>
                  <p className="text-xs text-dash-muted">
                    Closes {formatUtcDate(row.closing_date)}
                    {data.can_filter_agents && row.agent_name ? ` · ${row.agent_name}` : ""}
                    {row.price ? ` · ${formatMoney(row.price)} ${row.price_basis === "asking" ? "asking" : "sale"} price` : ""}
                  </p>
                </div>
                <div className="text-right">
                  {row.pct == null ? (
                    <Link to={`/deals/${row.deal_id}`} className="text-xs font-medium text-dash-amber hover:underline">No % set · add it</Link>
                  ) : (
                    <>
                      <p className="font-semibold tabular-nums text-dash-ink">{formatMoney(row.amount) || "—"}</p>
                      <p className="text-xs text-dash-muted">{row.pct}%</p>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {data.missing_pct > 0 && (
            <p className="mt-3 text-xs text-dash-faint">
              {data.missing_pct} deal{data.missing_pct === 1 ? "" : "s"} closing this month {data.missing_pct === 1 ? "has" : "have"} no compensation % yet, so {data.missing_pct === 1 ? "it isn't" : "they aren't"} in the total.
            </p>
          )}
        </>
      )}
    </SectionCard>
  );
}
